import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import { 
  bankMasterService, 
  chartOfAccountsService,
  getBankAccountTypeOptions, 
  getBankAccountStatusOptions 
} from '../../../../api/accounting';

/**
 * BankMasterTab Component
 * 
 * Manages bank accounts with integration to Chart of Accounts.
 * Each bank account must be linked to a chart of account (typically an asset account)
 * for proper double-entry bookkeeping and financial reporting.
 * 
 * Features:
 * - Create/Edit/Delete bank accounts
 * - Link bank accounts to chart of accounts
 * - Auto-create appropriate chart of accounts for new banks
 * - Validate chart of account selections (must be asset accounts)
 * - Display linked chart of account information
 */
const BankMasterTab = () => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [chartOfAccounts, setChartOfAccounts] = useState([]);

  useEffect(() => {
    fetchBankAccounts();
    fetchChartOfAccounts();
  }, [currentPage, searchTerm, filterType, filterStatus]);

  const fetchBankAccounts = async () => {
    setLoading(true);
    try {
      const result = await bankMasterService.getAll({
        page: currentPage,
        limit: 50,
        search: searchTerm,
        accountType: filterType,
        status: filterStatus
      });

      console.log(result);
      
      
      setBankAccounts(result.docs || []);
      setTotalPages(result.pagination?.total || 1);
      setTotalCount(result.pagination?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartOfAccounts = async () => {
    try {
      const result = await chartOfAccountsService.getAll({
        limit: 1000,
        isActive: true,
        accountType: 'assets', // Filter for asset accounts as bank accounts are assets
        accountCategory: 'current_assets'
      });
      
      
      // Handle both old and new response structures
      const accounts = result.docs || result.records || [];
      setChartOfAccounts(accounts);
    } catch (error) {
      console.error('Error fetching chart of accounts:', error);
      setChartOfAccounts([]);
    }
  };

  const resetForm = () => {
    setFormData({
      bankName: '',
      branchName: '',
      accountNumber: '',
      accountHolderName: '',
      accountType: '',
      chartOfAccountId: '', // Add chart of account reference
      ifscCode: '',
      swiftCode: '',
      openingBalance: 0,
      overdraftLimit: 0,
      interestRate: 0,
      isDefault: false,
      bankAddress: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      contactDetails: {
        phone: '',
        email: '',
        website: ''
      },
      status: 'active'
    });
  };

  const handleAdd = () => {
    resetForm();
    setSelectedAccount(null);
    setShowAddDialog(true);
  };

  const handleEdit = (account) => {
    setFormData({
      bankName: account.bankName || '',
      branchName: account.branchName || '',
      accountNumber: account.accountNumber || '',
      accountHolderName: account.accountHolderName || '',
      accountType: account.accountType || '',
      chartOfAccountId: account.chartOfAccountId || '', // Add chart of account reference
      ifscCode: account.ifscCode || '',
      swiftCode: account.swiftCode || '',
      openingBalance: account.openingBalance || 0,
      overdraftLimit: account.overdraftLimit || 0,
      interestRate: account.interestRate || 0,
      isDefault: account.isDefault || false,
      bankAddress: {
        street: account.bankAddress?.street || '',
        city: account.bankAddress?.city || '',
        state: account.bankAddress?.state || '',
        pincode: account.bankAddress?.pincode || '',
        country: account.bankAddress?.country || 'India'
      },
      contactDetails: {
        phone: account.contactDetails?.phone || '',
        email: account.contactDetails?.email || '',
        website: account.contactDetails?.website || ''
      },
      status: account.status || 'active'
    });
    setSelectedAccount(account);
    setShowEditDialog(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate chart of account selection
    const selectedChartAccount = chartOfAccounts.find(acc => acc._id === formData.chartOfAccountId);
    if (selectedChartAccount && selectedChartAccount.accountType !== 'assets') {
      alert('Please select an Asset account from the Chart of Accounts. Bank accounts should be linked to asset accounts.');
      return;
    }
    
    try {
      if (selectedAccount) {
        await bankMasterService.update(selectedAccount._id, formData);
      } else {
        await bankMasterService.create(formData);
      }
      
      setShowAddDialog(false);
      setShowEditDialog(false);
      setSelectedAccount(null);
      resetForm();
      fetchBankAccounts();
    } catch (error) {
      console.error('Error saving bank account:', error);
      alert('Error saving bank account: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bank account?')) {
      try {
        await bankMasterService.delete(id);
        fetchBankAccounts();
      } catch (error) {
        console.error('Error deleting bank account:', error);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      case 'frozen':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const createSuggestedBankAccount = async (bankName) => {
    try {
      // Generate a smart account code for bank accounts (typically in 1100-1199 range)
      const generateAccountCode = () => {
        const existingCodes = chartOfAccounts
          .filter(acc => acc.accountCode && acc.accountCode.startsWith('110'))
          .map(acc => parseInt(acc.accountCode))
          .filter(code => !isNaN(code))
          .sort((a, b) => a - b);
        
        // Find the next available code starting from 1100
        let nextCode = 1100;
        for (const code of existingCodes) {
          if (code === nextCode) {
            nextCode++;
          } else {
            break;
          }
        }
        return nextCode.toString();
      };

      const suggestedAccount = {
        accountCode: generateAccountCode(),
        accountName: `${bankName} - Bank Account`,
        accountType: 'assets',
        accountCategory: 'current_assets',
        reportClassification: 'balance_sheet',
        realPersonalType: 'real',
        description: `Bank account for ${bankName}`,
        allowsPosting: true,
        isActive: true,
        openingBalance: formData.openingBalance || 0
      };

      const newAccount = await chartOfAccountsService.create(suggestedAccount);
      await fetchChartOfAccounts(); // Refresh the list
      
      // Auto-select the newly created account
      setFormData(prev => ({ ...prev, chartOfAccountId: newAccount._id }));
      
      alert(`Chart of account "${newAccount.accountCode} - ${newAccount.accountName}" has been created and linked to this bank account.`);
    } catch (error) {
      console.error('Error creating suggested account:', error);
      alert('Failed to create chart of account: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Bank Accounts</h3>
          <p className="text-gray-600">Manage your bank accounts and payment methods</p>
          <div className="mt-2 flex items-center text-sm text-blue-600">
            <Icon name="Link" size={14} className="mr-1" />
            Integrated with Chart of Accounts for complete financial tracking
          </div>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Add Bank Account
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search bank accounts..."
          />
        </div>
        <div className="min-w-48">
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Account Types</option>
            {getBankAccountTypeOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-48">
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Statuses</option>
            {getBankAccountStatusOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bank Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Chart of Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : bankAccounts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No bank accounts found
                  </td>
                </tr>
              ) : (
                bankAccounts.map((account) => (
                  <tr key={account._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{account.bankName}</div>
                        {account.branchName && (
                          <div className="text-gray-500">{account.branchName}</div>
                        )}
                        <div className="text-gray-500">{account.accountHolderName}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {account.maskedAccountNumber || account.accountNumber}
                      {account.isDefault && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Default
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {/* Handle both chartOfAccountId (populated) and chartOfAccount (legacy) formats */}
                      {(account.chartOfAccountId || account.chartOfAccount) ? (
                        <div>
                          <div className="font-medium">
                            {(account.chartOfAccountId?.accountCode || account.chartOfAccount?.accountCode)}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {(account.chartOfAccountId?.accountName || account.chartOfAccount?.accountName)}
                          </div>
                          {(account.chartOfAccountId?.fullPath || account.chartOfAccount?.fullPath) && (
                            <div className="text-gray-400 text-xs mt-1">
                              Path: {(account.chartOfAccountId?.fullPath || account.chartOfAccount?.fullPath)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Not linked</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.accountType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{account.openingBalance?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(account.status)}`}>
                        {account.status?.replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleEdit(account)}
                          className="text-blue-600 hover:text-blue-900 transition-colors duration-200 p-1"
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button 
                          onClick={() => handleDelete(account._id)}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200 p-1"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-gray-700 text-center sm:text-left">
            Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, totalCount)} of {totalCount} results
          </div>
          <div className="flex justify-center sm:justify-end space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      {(showAddDialog || showEditDialog) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleFormSubmit}>
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {selectedAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
                </h3>
                
                {/* Basic Information */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-700 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bank Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.bankName || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, bankName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Branch Name
                      </label>
                      <input
                        type="text"
                        value={formData.branchName || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, branchName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.accountNumber || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account Holder Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.accountHolderName || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, accountHolderName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.accountType || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, accountType: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select Type</option>
                        {getBankAccountTypeOptions().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Chart of Account <span className="text-red-500">*</span>
                      </label>
                      {chartOfAccounts.length === 0 && (
                        <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-xs text-yellow-800">
                            <Icon name="AlertTriangle" size={14} className="inline mr-1" />
                            No asset accounts found in Chart of Accounts. You can create one using the + button below.
                          </p>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <select
                          value={formData.chartOfAccountId || ''}
                          onChange={(e) => {
                            const selectedAccountId = e.target.value;
                            const selectedAccount = chartOfAccounts.find(acc => acc._id === selectedAccountId);
                            
                            setFormData(prev => ({ 
                              ...prev, 
                              chartOfAccountId: selectedAccountId,
                              // Auto-populate opening balance from chart of account if available
                              ...(selectedAccount && selectedAccount.openingBalance && {
                                openingBalance: selectedAccount.openingBalance
                              })
                            }));
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="">Select Chart of Account</option>
                          {chartOfAccounts.length === 0 ? (
                            <option value="" disabled>No asset accounts found - Create one first</option>
                          ) : (
                            chartOfAccounts.map((account) => (
                              <option key={account._id} value={account._id}>
                                {account.accountCode} - {account.accountName}
                              </option>
                            ))
                          )}
                        </select>
                        <button
                          type="button"
                          onClick={fetchChartOfAccounts}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 whitespace-nowrap"
                          title="Refresh Chart of Accounts"
                        >
                          <Icon name="RefreshCw" size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const bankName = formData.bankName || 'New Bank';
                            if (window.confirm(`Create a chart of account for "${bankName}"? This will automatically create an appropriate asset account and link it to this bank account.`)) {
                              createSuggestedBankAccount(bankName);
                            }
                          }}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200 whitespace-nowrap"
                          title="Create New Chart of Account"
                          disabled={!formData.bankName}
                        >
                          <Icon name="Plus" size={16} />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        This links the bank account to your chart of accounts for proper transaction recording
                      </p>
                      {formData.chartOfAccountId && (() => {
                        const selectedAccount = chartOfAccounts.find(acc => acc._id === formData.chartOfAccountId);
                        return selectedAccount ? (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="text-xs text-blue-800">
                              <strong>Selected Account:</strong> {selectedAccount.accountCode} - {selectedAccount.accountName}
                              <br />
                              <strong>Type:</strong> {selectedAccount.accountType?.replace(/\b\w/g, l => l.toUpperCase())} 
                              | <strong>Category:</strong> {selectedAccount.accountCategory?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              {selectedAccount.openingBalance && (
                                <><br /><strong>Opening Balance:</strong> ₹{selectedAccount.openingBalance.toLocaleString()}</>
                              )}
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={formData.status || 'active'}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {getBankAccountStatusOptions().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-700 mb-3">Bank Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        IFSC Code
                      </label>
                      <input
                        type="text"
                        value={formData.ifscCode || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SWIFT Code
                      </label>
                      <input
                        type="text"
                        value={formData.swiftCode || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, swiftCode: e.target.value.toUpperCase() }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Bank Address */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-700 mb-3">Bank Address</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={formData.bankAddress?.street || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          bankAddress: { ...prev.bankAddress, street: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={formData.bankAddress?.city || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          bankAddress: { ...prev.bankAddress, city: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={formData.bankAddress?.state || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          bankAddress: { ...prev.bankAddress, state: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pincode
                      </label>
                      <input
                        type="text"
                        value={formData.bankAddress?.pincode || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          bankAddress: { ...prev.bankAddress, pincode: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <input
                        type="text"
                        value={formData.bankAddress?.country || 'India'}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          bankAddress: { ...prev.bankAddress, country: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-700 mb-3">Contact Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.contactDetails?.phone || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contactDetails: { ...prev.contactDetails, phone: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.contactDetails?.email || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contactDetails: { ...prev.contactDetails, email: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.contactDetails?.website || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contactDetails: { ...prev.contactDetails, website: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Financial Details */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-700 mb-3">Financial Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Opening Balance
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.openingBalance || 0}
                        onChange={(e) => setFormData(prev => ({ ...prev, openingBalance: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Overdraft Limit
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.overdraftLimit || 0}
                        onChange={(e) => setFormData(prev => ({ ...prev, overdraftLimit: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Interest Rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.interestRate || 0}
                        onChange={(e) => setFormData(prev => ({ ...prev, interestRate: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-700 mb-3">Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.isDefault || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Default Account</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddDialog(false);
                      setShowEditDialog(false);
                      setSelectedAccount(null);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    {selectedAccount ? 'Update' : 'Add'} Account
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankMasterTab;